import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { THEME } from '@/common/styles/theme';
import {
	ChatView,
	ChatLayout,
	ChatMainArea,
	ChatContainer,
	MessagesContainer,
	SidebarContainer,
	ConversationsListContainer,
} from './chat.styles';
import { getMessageGalleryMedia } from './utils/getConversationMediaFromMessages';
import useChatLogic from './hooks/useChatLogic';
import { useComposerAutoFocus } from './hooks/useComposerAutoFocus';
import { useChatAccess } from './hooks/useChatAccess';
import ChatSelectedProfile from './components/chatSelectedProfile/ChatSelectedProfile';
import ChatInputBar from './components/chatInputBar/ChatInputBar';
import Message from './components/message/Message';
import LoadingPlaceholders from './components/loadingPlaceholders/LoadingPlaceholders';
import {
	ChatComposerSkeletonBar,
	ChatMessagesSkeletonContent,
} from './components/chatConversationSkeleton/ChatConversationSkeleton';
import CompanionSidebar from './components/chatCompanion/CompanionSidebar';
import ImageGalleryModal from './components/imageGalleryModal/ImageGalleryModal';
import ConversationsList from './components/conversationList/ConversationList';
import ChatGuestGate from './components/chatGuestGate/ChatGuestGate';
import PlanCheckoutModal from '@/common/components/paywall/PlanCheckoutModal';
import BuyTokensModal from '@/common/components/layout/productLayout/components/topBar/components/BuyTokensModal/BuyTokensModal';
import {
	ANALYTICS_EVENT,
	ANALYTICS_MONETIZATION_SOURCE,
} from '@/common/constants/analyticsEvents';
import storageManager from '@/api/storageManager';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { useAnalytics } from '@/common/contexts/AnalyticsContext';

const EmptyStateContainer = styled.div`
	height: 100%;
	max-height: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	text-align: center;
	padding: 24px;
	color: ${THEME.muted};

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 16px ${THEME.mobileGutter};
	}
`;

const EmptyStateTitle = styled.h2`
	color: ${THEME.text};
	margin-bottom: 10px;
	font-family: ${THEME.fontDisplay};
`;

const EmptyStateText = styled.p`
	font-size: 1.05rem;
	max-width: 400px;
	color: rgba(216, 189, 228, 0.88);
	line-height: 1.45;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 0.9375rem;
		max-width: 100%;
	}
`;

const CHAT_PROFILE_WIDE_QUERY = '(min-width: 1081px)';

const getInitialProfilePanelOpen = () =>
	window.matchMedia(CHAT_PROFILE_WIDE_QUERY).matches;

const Chat = () => {
	const t = useLanguageData('chat');
	const navigate = useNavigate();
	const { id } = useParams();
	const location = useLocation();
	const { trackAnalytics } = useAnalytics();
	const chatReturnPath = `${location.pathname}${location.search}`;
	const messagesEndRef = useRef(null);
	const composerInputRef = useRef(null);
	const [isProfilePanelOpen, setIsProfilePanelOpen] = useState(
		getInitialProfilePanelOpen,
	);
	const [isTokensModalOpen, setIsTokensModalOpen] = useState(false);

	useEffect(() => {
		if (storageManager.payment.consumeError()) {
			setIsTokensModalOpen(true);
		}
	}, []);

	useEffect(() => {
		trackAnalytics(ANALYTICS_EVENT.CHAT.OPEN);
	}, []);

	const {
		needsRegistration,
		needsPlan,
		canChat,
		isAuthLoading,
		isCheckingAccount,
		openRegister,
		openLogin,
		refreshAccountStatus,
	} = useChatAccess();

	const handleTokensExhausted = useCallback(() => {
		setIsTokensModalOpen(true);
	}, []);

	const {
		person,
		imageSrc,
		messages,
		scoreProgress,
		isScoreProgressLoading,
		isGenerating,
		isTranscribing,
		isTextGenerating,
		isComposerLocked,
		isLoadingPerson,
		isLoadingConversation,
		message,
		isSendMode,
		setMessage,
		handleKeyPress,
		handleActionClick,
		handlePhotoRequest,
		handleVideoRequest,
		isGeneratingVideo,
		isRecording,
		handleCancelRecording,
		handleCall,
		endVoiceCall,
		isVoiceCallActive,
		voiceCallSeconds,
		voiceCallPhase,
		voiceWaveMode,
		conversationImages,
		isGalleryOpen,
		currentImageIndex,
		openGallery,
		closeGallery,
		handlePrev,
		handleNext,
	} = useChatLogic(messagesEndRef, {
		enabled: canChat,
		onTokensExhausted: handleTokensExhausted,
	});

	useComposerAutoFocus({
		inputRef: composerInputRef,
		conversationId: id,
		messages,
		isVoiceCallActive,
		isComposerLocked: isComposerLocked || isLoadingConversation,
		isLoadingConversation,
		isRecording,
		isTranscribing,
		isGalleryOpen,
	});

	useEffect(() => {
		const mediaQuery = window.matchMedia(CHAT_PROFILE_WIDE_QUERY);
		let wasWide = mediaQuery.matches;

		const syncProfilePanelForViewport = () => {
			const isWide = mediaQuery.matches;
			if (isWide === wasWide) {
				return;
			}

			wasWide = isWide;
			setIsProfilePanelOpen(isWide);
		};

		mediaQuery.addEventListener('change', syncProfilePanelForViewport);
		window.addEventListener('resize', syncProfilePanelForViewport);

		return () => {
			mediaQuery.removeEventListener('change', syncProfilePanelForViewport);
			window.removeEventListener('resize', syncProfilePanelForViewport);
		};
	}, []);

	useEffect(() => {
		if (!needsPlan) {
			return undefined;
		}

		const interval = window.setInterval(() => {
			refreshAccountStatus();
		}, 5000);

		return () => window.clearInterval(interval);
	}, [needsPlan, refreshAccountStatus]);

	const hasActiveConversation = useMemo(() => Boolean(id), [id]);
	const showActiveConversation = hasActiveConversation;
	const showMobileConversationList = !id;
	const showProfileSidebar = Boolean(person?._id) && !isLoadingPerson;

	const handleBackToConversations = useCallback(
		(event) => {
			event?.preventDefault?.();
			event?.stopPropagation?.();
			trackAnalytics(ANALYTICS_EVENT.CHAT.BACK_TO_LIST, {
				conversationId: id ?? null,
			});
			navigate('/chat');
		},
		[id, navigate],
	);

	const handleMessageMediaClick = useCallback(
		(msg) => {
			const media = getMessageGalleryMedia(msg);

			if (!media) {
				return;
			}

			const mediaIndex = conversationImages.findIndex(
				(item) => item.url === media.url,
			);

			if (mediaIndex !== -1) {
				trackAnalytics(ANALYTICS_EVENT.CHAT.MEDIA_OPEN, {
					conversationId: id ?? null,
					mediaType: media.type ?? 'image',
				});
				openGallery(mediaIndex);
			}
		},
		[conversationImages, id, openGallery],
	);

	const handleToggleProfilePanel = () => {
		setIsProfilePanelOpen((prev) => {
			const nextOpen = !prev;
			if (nextOpen) {
				trackAnalytics(ANALYTICS_EVENT.CHAT.PROFILE_OPEN, {
					personId: person?._id ?? id ?? null,
					conversationId: id ?? null,
				});
			}
			return nextOpen;
		});
	};

	const handleCompanionGalleryOpen = (...args) => {
		trackAnalytics(ANALYTICS_EVENT.CHAT.MEDIA_OPEN, {
			conversationId: id ?? null,
			mediaType: 'gallery',
		});
		openGallery(...args);
	};

	if (needsRegistration) {
		return (
			<ChatView id='chatView' data-view='chat'>
				<ChatGuestGate onRegister={openRegister} onLogin={openLogin} />
			</ChatView>
		);
	}

	if (isAuthLoading || isCheckingAccount) {
		return (
			<ChatView id='chatView' data-view='chat'>
				<ChatLayout id='chatLayout'>
					<ConversationsListContainer
						$mobileVisible={showMobileConversationList}
					>
						<ConversationsList />
					</ConversationsListContainer>
					<ChatMainArea
						$profilePanelOpen={false}
						$mobileHidden={showMobileConversationList}
					>
						<ChatContainer aria-label={t.conversationPanel}>
							<ChatSelectedProfile
								person={{}}
								imageSrc={null}
								isProfilePanelOpen={false}
								isVoiceCallActive={false}
								voiceCallSeconds={0}
								voiceWaveMode='silent'
								onCall={() => {}}
								onEndCall={() => {}}
								onToggleProfilePanel={() => {}}
							/>
							<MessagesContainer>
								<ChatMessagesSkeletonContent />
							</MessagesContainer>
							<ChatComposerSkeletonBar />
						</ChatContainer>
					</ChatMainArea>
				</ChatLayout>
			</ChatView>
		);
	}

	const chatContent = showActiveConversation ? (
		<ChatMainArea
			$profilePanelOpen={isProfilePanelOpen}
			$mobileHidden={showMobileConversationList}
		>
			<ChatContainer aria-label={t.conversationPanel}>
				<ChatSelectedProfile
					person={person}
					imageSrc={imageSrc}
					isProfilePanelOpen={isProfilePanelOpen}
					isVoiceCallActive={isVoiceCallActive}
					voiceCallSeconds={voiceCallSeconds}
					voiceWaveMode={voiceWaveMode}
					scoreProgress={scoreProgress}
					isScoreProgressLoading={isScoreProgressLoading}
					onCall={handleCall}
					onEndCall={endVoiceCall}
					onToggleProfilePanel={handleToggleProfilePanel}
					showBackButton={Boolean(id)}
					onBack={handleBackToConversations}
				/>
				<MessagesContainer>
					{isLoadingConversation ? (
						<ChatMessagesSkeletonContent />
					) : (
						<>
							{messages.map((msg, index) => (
								<Message
									key={index}
									msg={msg}
									person={person}
									onImageClick={() => handleMessageMediaClick(msg)}
								/>
							))}
							<LoadingPlaceholders
								isGenerating={isGenerating}
								isGeneratingVideo={isGeneratingVideo}
								isTextGenerating={isTextGenerating}
								isTranscribing={isTranscribing}
								isChecking={false}
							/>
							<div ref={messagesEndRef} />
						</>
					)}
				</MessagesContainer>
				<ChatInputBar
					ref={composerInputRef}
					message={message}
					setMessage={setMessage}
					handleKeyPress={handleKeyPress}
					isSendMode={isSendMode}
					handleActionClick={handleActionClick}
					onRequestPhoto={handlePhotoRequest}
					onRequestVideo={handleVideoRequest}
					isRecording={isRecording}
					handleCancelRecording={handleCancelRecording}
					isTranscribing={isTranscribing}
					isComposerLocked={isComposerLocked || isLoadingConversation}
					isVoiceCallActive={isVoiceCallActive}
					voiceCallPhase={voiceCallPhase}
					voiceWaveMode={voiceWaveMode}
				/>
			</ChatContainer>
			{showProfileSidebar && (
				<SidebarContainer
					$open={isProfilePanelOpen}
					aria-label={t.companionProfile}
				>
					<CompanionSidebar
						person={person}
						imageSrc={imageSrc}
						conversationImages={conversationImages}
						openGallery={handleCompanionGalleryOpen}
						onClosePanel={() => setIsProfilePanelOpen(false)}
					/>
				</SidebarContainer>
			)}
		</ChatMainArea>
	) : (
		!showMobileConversationList && (
			<EmptyStateContainer>
				<EmptyStateTitle>{t.noConversationTitle}</EmptyStateTitle>
				<EmptyStateText>{t.noConversationText}</EmptyStateText>
			</EmptyStateContainer>
		)
	);

	return (
		<ChatView id='chatView' data-view='chat'>
			<ChatLayout id='chatLayout'>
				<ConversationsListContainer $mobileVisible={showMobileConversationList}>
					<ConversationsList />
				</ConversationsListContainer>
				{chatContent}
				{isGalleryOpen && (
					<ImageGalleryModal
						images={conversationImages}
						currentIndex={currentImageIndex}
						onClose={closeGallery}
						onPrev={handlePrev}
						onNext={handleNext}
					/>
				)}
			</ChatLayout>
			{needsPlan && (
				<PlanCheckoutModal
					secured
					returnPath={chatReturnPath}
					source={ANALYTICS_MONETIZATION_SOURCE.CHAT}
				/>
			)}
			{isTokensModalOpen && (
				<BuyTokensModal
					returnPath={chatReturnPath}
					source={ANALYTICS_MONETIZATION_SOURCE.CHAT}
					onClose={() => setIsTokensModalOpen(false)}
					headerTitle={t.tokensExhaustedTitle}
					description={t.tokensExhaustedDescription}
					heroTitle={t.tokensExhaustedHeroTitle}
					heroDescription={t.tokensExhaustedHeroDescription}
				/>
			)}
		</ChatView>
	);
};

export default Chat;
